import { createPortal } from 'react-dom';
import { C, t } from '../constants/dashboardConstants';
import { getScheduleAccentBackground, normalizeScheduleColor } from './scheduleColors';

const KO_DAYS = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];

export default function EventDetailModal({ event, onClose, onDelete, onEdit }) {
  const start = new Date(event.startAt);
  const end = event.endAt ? new Date(event.endAt) : null;
  const color = normalizeScheduleColor(event.color || event.eventColor);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  start.setSeconds(0, 0);
  const eventDay = new Date(start);
  eventDay.setHours(0, 0, 0, 0);
  const diff = Math.floor((today - eventDay) / (1000 * 60 * 60 * 24));
  const dLabel = diff > 0 ? `D+${diff}` : diff < 0 ? `D${diff}` : 'D-Day';
  const isPast = diff > 0;

  const startDay = new Date(event.startAt);
  const dateLabel = `${startDay.getFullYear()}년 ${startDay.getMonth() + 1}월 ${startDay.getDate()}일 ${KO_DAYS[startDay.getDay()]}`;
  const timeLabel = `${String(startDay.getHours()).padStart(2, '0')}:${String(startDay.getMinutes()).padStart(2, '0')}`;
  const endTimeLabel = end
    ? `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`
    : null;
  const showActions = Boolean(onDelete || onEdit);

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: C.white, borderRadius: '20px',
          padding: '60px 50px', width: '780px', boxSizing: 'border-box',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ width: '12px', height: '12px', borderRadius: '999px', background: color, flexShrink: 0 }} />
            <span style={t('36px', 600, C.bk)}>{event.title}</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke={C.g400} strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Info card */}
        <div style={{
          background: C.bg, border: `1px solid ${color}`,
          borderRadius: '12px', overflow: 'hidden', marginBottom: '40px',
        }}>
          {/* D-Day */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '24px 30px', borderBottom: `1px solid ${color}`,
            background: getScheduleAccentBackground(color),
          }}>
            <span style={t('28px', 700, color)}>{dLabel}</span>
            {isPast && <span style={t('16px', 400, C.sub)}>지난 일정</span>}
          </div>

          {/* Date / Time / Location */}
          <div style={{ padding: '24px 30px', borderBottom: event.description ? `1px solid ${C.g200}` : 'none' }}>
            <div style={{ display: 'flex', gap: '60px' }}>
              <div>
                <div style={t('14px', 400, C.sub)}>일정</div>
                <div style={{ ...t('18px', 400, C.bk), marginTop: '8px' }}>{dateLabel}</div>
              </div>
              <div>
                <div style={t('14px', 400, C.sub)}>시간</div>
                <div style={{ ...t('18px', 400, C.bk), marginTop: '8px' }}>
                  {timeLabel}{endTimeLabel ? ` ~ ${endTimeLabel}` : ''}
                </div>
              </div>
              {event.location && (
                <div>
                  <div style={t('14px', 400, C.sub)}>장소</div>
                  <div style={{ ...t('18px', 400, C.bk), marginTop: '8px' }}>{event.location}</div>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          {event.description && (
            <div style={{ padding: '24px 30px' }}>
              <div style={t('14px', 400, C.sub)}>세부 정보</div>
              <div style={{ ...t('16px', 400, C.bk), marginTop: '8px' }}>{event.description}</div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        {showActions && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
          {onDelete && <button onClick={onDelete} style={{
            height: '50px', padding: '0 30px', borderRadius: '25px',
            background: C.g200, border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '8px',
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M3 6h18M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" stroke={C.bk} strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <span style={t('16px', 500, C.bk)}>삭제</span>
          </button>}
          {onEdit && <button onClick={onEdit} style={{
            height: '50px', padding: '0 30px', borderRadius: '25px',
            background: C.bk, border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '8px',
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke={C.white} strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke={C.white} strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <span style={t('16px', 500, C.white)}>수정</span>
          </button>}
        </div>
        )}
      </div>
    </div>,
    document.body
  );
}
